import { computed, readonly, ref } from 'vue'
import { getOperationForType } from './inbound/constants/operationTypes'
import integrations from './../../service'

/**
 * A service for the management of the SFTP integration
 * @returns {*}
 */
export default (account, agency, config) => {
  const outboundInitialised = ref(false)
  const outboundProfiles = ref([])

  const inboundInitialised = ref(false)
  const inboundSettings = ref({})
  const inboundOperations = ref([])
  const inboundQuotas = ref([])
  const inboundOperationsAndQuotasLoaded = ref(false)

  const working = ref(false)
  const api = integrations(config.urlFactory)

  return {
    /**
     * Return the current drilled down account
     * @returns {*}
     */
    get account() {
      return account
    },

    /**
     * Return the current drilled down agency
     * @returns {*}
     */
    get agency() {
      return agency
    },

    /**
     * Determine if the component is awaiting processing
     * @returns {ComputedRef<boolean>}
     */
    get working() {
      return computed(() => working.value)
    },

    /**
     * Return a list of preset lead fields
     * @returns {*}
     */
    getPresetLeadFields() {
      return config.leadPresetFields()
    },

    /**
     * Initialise the service for the current drilled down account
     * @returns {Promise<void>}
     */
    async init() {
      await Promise.all([this.initInbound(), this.initOutbound()])
    },

    /**
     * Initialise the service for the SFTP inbound integration
     * @returns {Promise<void>}
     */
    async initInbound() {
      await this.initInboundSettings()
      if (this.inboundSettings.username)
        await this.refreshInboundOperationsAndQuotas()
    },

    /**
     * Initialise the service for the SFTP outbound integration
     * @returns {Promise<void>}
     */
    async initOutbound() {
      await this.initOutboundProfiles()
    },

    /**
     * Return the initialised state for the outbound integration
     */
    get outboundInitialised() {
      return computed(() => outboundInitialised.value)
    },

    /**
     * Refresh inbound operations and quotas
     * @returns {Promise<void>}
     */
    async refreshInboundOperationsAndQuotas() {
      await Promise.all([
        this.initInboundOperations(),
        this.initInboundQuotas(),
      ])

      inboundOperationsAndQuotasLoaded.value = true
    },

    /**
     * Return the initialised state for the inbound integration
     * @returns {ComputedRef<boolean>}
     */
    get inboundInitialised() {
      return computed(() => inboundInitialised.value)
    },

    /* ================= SFTP OUTBOUND PROFILES  ================= */

    /**
     * Initialise SFTP outbound profiles
     * @returns {Promise<void>}
     */
    async initOutboundProfiles() {
      const endpoint = `/sftp/outbound/profile?account=${account}&agency=${agency}`
      try {
        const response = await api.request(endpoint)
        if (response.status === 200) {
          outboundProfiles.value = await response.json()
          outboundInitialised.value = true
        } else {
          // todo: report error
        }
      } catch (err) {
        // todo: report error
      }
    },

    /**
     * Return the list of outbound profiles
     * @returns {ComputedRef<*>}
     */
    get outboundProfiles() {
      return computed(() => outboundProfiles.value)
    },

    /**
     * Return outbound profile table data
     * @returns {ComputedRef<*>}
     */
    get outboundProfilesTableData() {
      return computed(() =>
        this.outboundProfiles.value.map((profile) => ({
          _id: profile._id,
          name: profile.name,
          location: `sftp://${profile.host}:${profile.port}`,
        })),
      )
    },

    /**
     * Process an outbound profile event
     * @param {String} event
     * @param {*} data
     * @returns {Promise<*>}
     */
    async processOutboundProfile(event, data) {
      switch (event) {
        case 'create':
          return this.handleOutboundProfileCreate(data)
        case 'update':
          return this.handleOutboundProfileUpdate(data)
        case 'delete':
          return this.handleOutboundProfileDelete(data)
        default:
          return Promise.resolve()
      }
    },

    /**
     * Process the creation of a new profile
     * @param {Object} data
     * @returns {Promise<*>}
     */
    async handleOutboundProfileCreate(data) {
      const endpoint = `/sftp/outbound/profile?account=${account}&agency=${agency}`
      const options = {
        method: 'POST',
        body: JSON.stringify(data),
      }
      const headers = {
        'Content-Type': 'application/json',
      }
      try {
        const response = await api.request(endpoint, options, headers)
        const data = await response.json()
        if (response.status === 200) {
          outboundProfiles.value = outboundProfiles.value.concat(data)
          return { success: true, data }
        } else if (response.status === 422) {
          return { success: false, errors: data.errors }
        }
      } catch (err) {
        console.log('error adding profile with error: ', err)
      }
      return { success: false }
    },

    /**
     * Process the update of a new operation
     * @param {Object} data
     * @returns {Promise<*>}
     */
    async handleOutboundProfileUpdate(data) {
      const endpoint = `/sftp/outbound/profile/${data._id}?account=${account}&agency=${agency}`
      const options = {
        method: 'PUT',
        body: JSON.stringify(data),
      }
      const headers = {
        'Content-Type': 'application/json',
      }
      try {
        const response = await api.request(endpoint, options, headers)
        const data = await response.json()
        if (response.status === 200) {
          const idx = outboundProfiles.value.findIndex(
            (entry) => entry._id === data._id,
          )
          if (idx > -1) outboundProfiles.value[idx] = data
          return { success: true }
        } else if (response.status === 422) {
          return { success: false, errors: data.errors }
        }
      } catch (err) {
        console.log('error updating profile with error: ', err)
      }
      return { success: false }
    },

    /**
     * Process the deletion of an existing profile
     * @param {Object} data
     * @returns {Promise<*>}
     */
    async handleOutboundProfileDelete(data) {
      const endpoint = `/sftp/outbound/profile/${data._id}?account=${account}&agency=${agency}`
      const options = {
        method: 'DELETE',
      }
      try {
        const response = await api.request(endpoint, options)
        if (response.status === 204) {
          outboundProfiles.value = outboundProfiles.value.filter(
            (entry) => entry._id !== data._id,
          )
          return { success: true }
        }
      } catch (err) {
        console.log('error deleting profile with error: ', err)
      }
      return { success: false }
    },

    /* ================= SFTP INBOUND SETTINGS  ================= */

    /**
     * Initialise SFTP inbound settings
     * @returns {Promise<void>}
     */
    async initInboundSettings() {
      const endpoint = `/sftp/inbound/settings?account=${account}&agency=${agency}`
      try {
        const response = await api.request(endpoint)
        if (response.status === 200) {
          inboundSettings.value = await response.json()
          inboundInitialised.value = true
        } else {
          // todo: report error
        }
      } catch (err) {
        // todo: report error
      }
    },

    /**
     * Update SFTP inbound settings
     * @returns {Promise<void>}
     */
    async updateInboundSettings(settings) {
      const endpoint = `/sftp/inbound/settings?account=${account}&agency=${agency}`
      const options = {
        method: 'PUT',
        body: JSON.stringify(settings),
      }
      const headers = {
        'Content-Type': 'application/json',
      }
      try {
        const response = await api.request(endpoint, options, headers)
        const data = await response.json()
        if (response.status === 200) {
          inboundSettings.value = { ...inboundSettings.value, ...data }
        } else {
          return Promise.reject(new Error('unable to update settings'))
        }
      } catch (err) {
        return Promise.reject(new Error('unable to update settings'))
      }
    },

    /**
     * Return a read only copy of the SFTP inbound settings
     * @returns {*}
     */
    get inboundSettings() {
      return readonly(inboundSettings.value)
    },

    /**
     * Determine if the SFTP inbound integration has loaded operations and quotas
     * @returns {ComputedRef<boolean>}
     */
    get inboundOperationsAndQuotasLoaded() {
      return computed(() => inboundOperationsAndQuotasLoaded.value)
    },

    /**
     * Handle an credentials update
     * @param {String} action
     * @param {boolean} withRefresh
     * @returns {Promise<void>}
     */
    async handleInboundCredentialsAction(action, withRefresh = true) {
      const endpoint = `/sftp/inbound/authentication/${action}?account=${account}&agency=${agency}`
      const options = {
        method: 'POST',
      }
      try {
        working.value = true
        const response = await api.request(endpoint, options)
        const data = await response.json()
        if (response.status === 200) {
          inboundSettings.value = { ...inboundSettings.value, ...data }
          if (withRefresh) await this.refreshInboundOperationsAndQuotas()
        } else {
          // todo: report error
        }
      } catch (err) {
        // todo: report error
      } finally {
        working.value = false
      }
    },

    /**
     * Generate SFTP inbound credentials
     * @returns {Promise<void>}
     */
    async handleInboundCredentialsGenerate() {
      return this.handleInboundCredentialsAction('generate')
    },

    /**
     * Refresh SFTP inbound credentials
     * @returns {Promise<void>}
     */
    async handleInboundCredentialsRefresh() {
      return this.handleInboundCredentialsAction('generate', false)
    },

    /**
     * Revoke SFTP inbound credentials
     * @returns {Promise<void>}
     */
    async handleInboundCredentialsRevoke() {
      return this.handleInboundCredentialsAction('revoke', false)
    },

    /* ================= SFTP INBOUND OPERATIONS  ================= */

    /**
     * Initialise inbound operations data
     * @returns {Promise<void>}
     */
    async initInboundOperations() {
      const endpoint = `/sftp/inbound/operation?account=${account}&agency=${agency}`
      try {
        const response = await api.request(endpoint)
        inboundOperations.value = await response.json()
      } catch (err) {
        // todo: report error
      }
    },

    /**
     * Return the list of inbound operations
     * @returns {ComputedRef<*>}
     */
    get inboundOperations() {
      return computed(() => inboundOperations.value)
    },

    /**
     * Return the number of inbound operations
     * @returns {ComputedRef<*>}
     */
    get inboundOperationsCount() {
      return computed(() => this.inboundOperations.value.length)
    },

    /**
     * Return inbound operation table data
     * @returns {ComputedRef<*>}
     */
    get inboundOperationsTableData() {
      return computed(() =>
        this.inboundOperations.value.map((operation) => ({
          uuid: operation.uuid,
          name: operation.name,
          label: getOperationForType(operation.type).label(),
        })),
      )
    },

    /**
     * Process an inbound operation event
     * @param {String} event
     * @param {*} data
     * @returns {Promise<*>}
     */
    async processInboundOperation(event, data) {
      switch (event) {
        case 'create':
          return this.handleInboundOperationCreate(data)
        case 'update':
          return this.handleInboundOperationUpdate(data)
        case 'delete':
          return this.handleInboundOperationDelete(data)
        default:
          return Promise.resolve()
      }
    },

    /**
     * Process the creation of a new operation
     * @param {Object} data
     * @returns {Promise<*>}
     */
    async handleInboundOperationCreate(data) {
      const endpoint = `/sftp/inbound/operation?account=${account}&agency=${agency}`
      const options = {
        method: 'POST',
        body: JSON.stringify(data),
      }
      const headers = {
        'Content-Type': 'application/json',
      }
      try {
        const response = await api.request(endpoint, options, headers)
        const data = await response.json()
        if (response.status === 200) {
          inboundOperations.value = inboundOperations.value.concat(data)
          return { success: true }
        } else if (response.status === 422) {
          return { success: false, errors: data.errors }
        }
      } catch (err) {
        console.log('error adding operation with error: ', err)
      }
      return { success: false }
    },

    /**
     * Process the update of a new operation
     * @param {Object} data
     * @returns {Promise<*>}
     */
    async handleInboundOperationUpdate(data) {
      const endpoint = `/sftp/inbound/operation/${data._id}?account=${account}&agency=${agency}`
      const options = {
        method: 'PUT',
        body: JSON.stringify(data),
      }
      const headers = {
        'Content-Type': 'application/json',
      }
      try {
        const response = await api.request(endpoint, options, headers)
        const data = await response.json()
        if (response.status === 200) {
          const idx = inboundOperations.value.findIndex(
            (entry) => entry._id === data._id,
          )
          if (idx > -1) inboundOperations.value[idx] = data
          return { success: true }
        } else if (response.status === 422) {
          return { success: false, errors: data.errors }
        }
      } catch (err) {
        console.log('error updating operation with error: ', err)
      }
      return { success: false }
    },

    /**
     * Process the deletion of an existing operation
     * @param {Object} data
     * @returns {Promise<*>}
     */
    async handleInboundOperationDelete(data) {
      const endpoint = `/sftp/inbound/operation/${data._id}?account=${account}&agency=${agency}`
      const options = {
        method: 'DELETE',
      }
      try {
        const response = await api.request(endpoint, options)
        if (response.status === 204) {
          inboundOperations.value = inboundOperations.value.filter(
            (entry) => entry._id !== data._id,
          )
          return { success: true }
        }
      } catch (err) {
        console.log('error deleting operation with error: ', err)
      }
      return { success: false }
    },

    /* ================= SFTP INBOUND QUOTAS  ================= */

    /**
     * Initialise inbound quotas
     * @returns {Promise<void>}
     */
    async initInboundQuotas() {
      const endpoint = `/sftp/inbound/quota?account=${account}&agency=${agency}`
      try {
        const response = await api.request(endpoint)
        inboundQuotas.value = await response.json()
      } catch (err) {
        // todo: report error
      }
    },

    /**
     * Return the first quota of the given type
     * @param {String} type
     * @returns {ComputedRef<T>}
     */
    getFirstQuotaByType(type) {
      return computed(() =>
        inboundQuotas.value.find((quota) => quota.type === type),
      )
    },
  }
}
